import { useEffect, useState } from 'react';

export function CircularTimer({ 
  duration = 30, 
  size = 80, 
  strokeWidth = 6,
  onComplete,
  isRunning = true,
  label = "Processing..."
}) {
  const [timeLeft, setTimeLeft] = useState(duration);
  const [isComplete, setIsComplete] = useState(false);
  
  const radius = (size - strokeWidth) / 2;
  const circumference = radius * 2 * Math.PI;
  const progress = ((duration - timeLeft) / duration) * 100;
  const strokeDashoffset = circumference - (progress / 100) * circumference;

  useEffect(() => {
    setTimeLeft(duration);
    setIsComplete(false);
  }, [duration]);

  useEffect(() => {
    if (!isRunning || timeLeft <= 0) {
      if (timeLeft <= 0 && !isComplete) {
        setIsComplete(true);
        onComplete?.();
      }
      return;
    }

    const timer = setInterval(() => {
      setTimeLeft((prev) => {
        if (prev <= 1) {
          clearInterval(timer);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [isRunning, timeLeft, isComplete, onComplete]);

  const formatTime = (seconds) => {
    if (seconds >= 60) {
      const mins = Math.floor(seconds / 60);
      const secs = seconds % 60;
      return `${mins}:${secs.toString().padStart(2, '0')}`;
    }
    return `${seconds}s`;
  };

  // Determine color based on progress
  const getColor = () => {
    if (progress >= 80) return '#22c55e'; // green
    if (progress >= 50) return '#f59e0b'; // amber
    return '#6366f1'; // indigo
  };

  return (
    <div className="flex flex-col items-center gap-2">
      <div className="relative" style={{ width: size, height: size }}>
        {/* Background circle */}
        <svg
          className="transform -rotate-90"
          width={size}
          height={size}
        >
          <circle
            cx={size / 2}
            cy={size / 2}
            r={radius}
            fill="none"
            stroke="#e5e7eb"
            strokeWidth={strokeWidth}
          />
          {/* Progress circle */}
          <circle
            cx={size / 2}
            cy={size / 2}
            r={radius}
            fill="none"
            stroke={getColor()}
            strokeWidth={strokeWidth}
            strokeLinecap="round"
            strokeDasharray={circumference}
            strokeDashoffset={strokeDashoffset}
            style={{ transition: 'stroke-dashoffset 0.5s ease, stroke 0.3s ease' }}
          />
        </svg>
        {/* Time display */}
        <div 
          className="absolute inset-0 flex items-center justify-center"
          style={{ fontSize: size * 0.22 }}
        >
          <span className="font-bold text-slate-700">
            {isComplete ? '✓' : formatTime(timeLeft)}
          </span>
        </div>
      </div>
      {label && (
        <span className="text-sm text-slate-600 text-center max-w-[120px]">
          {isComplete ? 'Complete!' : label}
        </span>
      )}
    </div>
  );
}

export function CircularProgress({ 
  progress = 0, 
  size = 60, 
  strokeWidth = 5,
  showPercentage = true,
  label
}) {
  const radius = (size - strokeWidth) / 2;
  const circumference = radius * 2 * Math.PI;
  const strokeDashoffset = circumference - (progress / 100) * circumference;

  const getColor = () => {
    if (progress >= 80) return '#22c55e';
    if (progress >= 50) return '#f59e0b';
    return '#6366f1';
  };

  return (
    <div className="flex flex-col items-center gap-1">
      <div className="relative" style={{ width: size, height: size }}>
        <svg className="transform -rotate-90" width={size} height={size}>
          <circle
            cx={size / 2}
            cy={size / 2}
            r={radius}
            fill="none"
            stroke="#e5e7eb"
            strokeWidth={strokeWidth}
          />
          <circle
            cx={size / 2}
            cy={size / 2}
            r={radius}
            fill="none"
            stroke={getColor()}
            strokeWidth={strokeWidth}
            strokeLinecap="round"
            strokeDasharray={circumference}
            strokeDashoffset={strokeDashoffset}
            style={{ transition: 'stroke-dashoffset 0.3s ease' }}
          />
        </svg>
        {showPercentage && (
          <div 
            className="absolute inset-0 flex items-center justify-center"
            style={{ fontSize: size * 0.25 }}
          >
            <span className="font-bold text-slate-700">{Math.round(progress)}%</span>
          </div>
        )}
      </div>
      {label && <span className="text-xs text-slate-500">{label}</span>}
    </div>
  );
}
